import { DPIA_CRITERIA_CODE_MAX_LENGTH } from 'config/api/dpiaRequests/dpiaRequests.constants';
import { calculateDpiaRecommendation, doesDecisionDeviateFromRecommendation } from 'config/api/dpiaRequests/dpiaRequests.helpers';
import type {
  DpiaRequest,
  DpiaRequestDecision,
  DpiaScreeningCriterionCode,
  DpiaRequestTriggerSource,
  SaveDpiaRequestDraftInput,
} from 'config/api/dpiaRequests/dpiaRequests.types';

export type DpiaNeedAssessmentFormValues = {
  triggerSource: DpiaRequestTriggerSource;
  triggerComment: string;
  selectedCriteria: DpiaScreeningCriterionCode[];
  criteriaAssessmentComment: string;
  alwaysDpiaRuleTriggered: boolean;
  alwaysDpiaRuleComment: string;
  decision: DpiaRequestDecision | '';
  decisionComment: string;
};

export type DpiaNeedAssessmentValidationMessages = {
  required: string;
  triggerComment: string;
  alwaysRuleComment: string;
  decisionComment: string;
  decisionRequired: string;
  criteriaCodesMaxLength: string;
};

export const mapRequestToFormValues = (request: DpiaRequest): DpiaNeedAssessmentFormValues => ({
  triggerSource: request.triggerSource,
  triggerComment: request.triggerComment,
  selectedCriteria: request.selectedCriteria,
  criteriaAssessmentComment: request.criteriaAssessmentComment,
  alwaysDpiaRuleTriggered: request.alwaysDpiaRuleTriggered,
  alwaysDpiaRuleComment: request.alwaysDpiaRuleComment,
  decision: request.decision ?? '',
  decisionComment: request.decisionComment,
});

export const mapFormValuesToDraftInput = (values: DpiaNeedAssessmentFormValues): SaveDpiaRequestDraftInput => ({
  triggerSource: values.triggerSource,
  triggerComment: values.triggerComment,
  selectedCriteria: values.selectedCriteria,
  criteriaAssessmentComment: values.criteriaAssessmentComment,
  alwaysDpiaRuleTriggered: values.alwaysDpiaRuleTriggered,
  alwaysDpiaRuleComment: values.alwaysDpiaRuleComment,
});

export const getLiveRecommendation = (values: DpiaNeedAssessmentFormValues) =>
  calculateDpiaRecommendation(values.selectedCriteria, values.alwaysDpiaRuleTriggered);

export const getLiveDecisionDeviation = (values: DpiaNeedAssessmentFormValues) => {
  if (!values.decision) {
    return false;
  }

  return doesDecisionDeviateFromRecommendation(values.decision, getLiveRecommendation(values));
};

export const validateDraftForm = (
  values: DpiaNeedAssessmentFormValues,
  messages: DpiaNeedAssessmentValidationMessages,
): Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> => {
  const errors: Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> = {};

  if (!values.triggerComment.trim()) {
    errors.triggerComment = messages.triggerComment;
  }

  if (values.alwaysDpiaRuleTriggered && !values.alwaysDpiaRuleComment.trim()) {
    errors.alwaysDpiaRuleComment = messages.alwaysRuleComment;
  }

  const hasOversizedCriteria = values.selectedCriteria.some(code => code.length > DPIA_CRITERIA_CODE_MAX_LENGTH);
  if (hasOversizedCriteria) {
    errors.selectedCriteria = messages.criteriaCodesMaxLength;
  }

  return errors;
};

export const validateDecisionForm = (
  values: DpiaNeedAssessmentFormValues,
  messages: DpiaNeedAssessmentValidationMessages,
): Partial<Record<keyof DpiaNeedAssessmentFormValues | 'form', string>> => {
  const errors = validateDraftForm(values, messages);

  if (!values.decision) {
    errors.decision = messages.decisionRequired;
  }

  if (!values.decisionComment.trim()) {
    errors.decisionComment = messages.decisionComment;
  }

  return errors;
};

export const hasValidationErrors = (errors: Partial<Record<string, string>>) => Object.keys(errors).length > 0;

export const isDpiaRequestEditable = (status: DpiaRequest['status']) => status === 'draft';
